import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { Button, Container } from "@wojo/ui";
import React, { useEffect, useId } from "react";
import ScrollSpy from "react-scrollspy-navigation";
import { useTourPageContext } from "../../_client/context";
import {
    scrollToSection,
    TourSectionId,
    useTourPageSectionConfiguration,
} from "../../_client/hooks";
import styles from "./TourNav.module.scss";

type TourNavProps = {
    displayTripBuilderAnchor?: boolean;
};

export const TourNav: React.FC<TourNavProps> = ({
    displayTripBuilderAnchor,
}) => {
    const { navRef, pageConfig } = useTourPageContext();
    const { isSectionDisplayed } = useTourPageSectionConfiguration();
    const i18n = useI18n("productPage");
    const navLabels = i18n.productPage.navigation;
    const navId = useId();
    const tripBuilderHash = "#tripbuilder";

    const sections = pageConfig.tourPageSectionOrder.map(
        (sectionBlok) => sectionBlok.section as TourSectionId,
    );

    const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const anchorText = event.currentTarget.hash.slice(1);
        const element = document.getElementById(anchorText);
        if (element) {
            gtmSendEvent({
                event: "track",
                ga4Tracking: {
                    ga4_object: "SUB_NAV",
                    ga4_action: "CLICKED",
                    eventCategory: "product page",
                    eventAction: "nav clicks",
                    eventLabel: anchorText,
                    menuText:
                        event.currentTarget.textContent?.trim() || anchorText,
                },
            });
        }
        scrollToSection(event, navRef);
    };

    useEffect(() => {
        scrollToSection(window.location.hash, navRef);
    }, [navRef]);

    return (
        <div className={styles["anchor-nav-container"]}>
            <Container>
                <nav
                    ref={navRef}
                    className={styles["anchor-nav"]}
                    aria-label={i18n.productPage.navigation.heading.l()}
                    id={navId}
                >
                    <ul aria-labelledby={navId}>
                        <ScrollSpy activeClass="active-anchor">
                            {sections.map(
                                (section: TourSectionId, index: number) => (
                                    <li
                                        key={`${section}_${index}`}
                                        style={{
                                            display: !isSectionDisplayed(
                                                section,
                                            )
                                                ? "none"
                                                : undefined,
                                        }}
                                    >
                                        <a
                                            href={`#${section}`}
                                            className={styles.anchor}
                                            onClick={handleAnchorClick}
                                        >
                                            {navLabels[section].label.l() || ""}
                                        </a>
                                    </li>
                                ),
                            )}
                            <li className={styles["mobile-only"]}>
                                <a
                                    href={tripBuilderHash}
                                    className={styles.anchor}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        gtmSendEvent({
                                            event: "track",
                                            ga4Tracking: {
                                                ga4_object: "TRIP_BUILDER",
                                                ga4_action: "STICKY_BUTTON",
                                            },
                                        });
                                        scrollToSection(e, navRef);
                                    }}
                                >
                                    {navLabels.tripbuilder.label.l()}
                                </a>
                            </li>
                        </ScrollSpy>
                    </ul>
                    {displayTripBuilderAnchor && (
                        <Button
                            href={tripBuilderHash}
                            onClick={(e) => {
                                e.preventDefault();
                                gtmSendEvent({
                                    event: "track",
                                    ga4Tracking: {
                                        ga4_object: "TRIP_BUILDER",
                                        ga4_action: "STICKY_BUTTON",
                                    },
                                });
                                scrollToSection(tripBuilderHash, navRef);
                            }}
                            tag="a"
                            variant="primary"
                            style={{ alignSelf: "center" }}
                            className={styles["desktop-only"]}
                        >
                            {i18n.productPage.tourHero.tripBuilderButtonLabel.l()}
                        </Button>
                    )}
                </nav>
            </Container>
        </div>
    );
};
